import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { RequirePermission } from "@/components/admin/RequirePermission";
import { adminCorporateDashboardQuery, adminQuotesQuery } from "@/lib/corporate/queries";
import { formatDate, formatPaise } from "@/lib/corporate/format";
import { CORPORATE_QUOTE_STATUSES, QUOTE_STATUS_LABEL, type CorporateQuoteStatus } from "@/lib/corporate/types";

export const Route = createFileRoute("/admin/quotes/")({ component: () => (
  <RequirePermission perm="orders.read"><QuotesConsole /></RequirePermission>
) });

function QuotesConsole() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<CorporateQuoteStatus | "all">("all");
  const filters = useMemo(
    () => ({ ...(search.trim() ? { search: search.trim() } : {}), status }),
    [search, status],
  );
  const { data, isLoading, error } = useQuery(adminQuotesQuery(filters));
  const dash = useQuery(adminCorporateDashboardQuery());
  const quotes = data?.quotes ?? [];
  const d = dash.data?.dashboard;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Corporate Quotes</h2>
        <p className="mt-1 text-sm text-black/55">Pipeline, quote builder and conversion to order.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Metric label="Revenue (converted)" value={formatPaise(d?.revenueCents ?? 0)} />
        <Metric label="Open pipeline" value={formatPaise(d?.pipelineCents ?? 0)} />
        <Metric label="Average quote value" value={formatPaise(d?.averageOrderValueCents ?? 0)} />
        <Metric label="Conversion rate" value={`${Math.round((d?.conversionRate ?? 0) * 100)}%`} />
        <Metric label="Total quotes" value={String(d?.totalQuotes ?? 0)} />
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-sm border border-black/10 bg-white p-3">
        <input
          className="w-72 border border-black/15 px-3 py-2 text-sm"
          placeholder="Search quote no., company, contact…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="border border-black/15 px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value as never)}>
          <option value="all">All statuses</option>
          {CORPORATE_QUOTE_STATUSES.map((s) => <option key={s} value={s}>{QUOTE_STATUS_LABEL[s]}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto rounded-sm border border-black/10 bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="border-b border-black/10 text-[10px] uppercase tracking-[0.18em] text-black/50">
            <tr>
              <th className="px-4 py-3">Quote</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Sales rep</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Value</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td className="px-4 py-6 text-black/60" colSpan={7}>Loading quotes…</td></tr>}
            {error && <tr><td className="px-4 py-6 text-red-700" colSpan={7}>{(error as Error).message}</td></tr>}
            {!isLoading && !error && quotes.length === 0 && (
              <tr><td className="px-4 py-6 text-black/60" colSpan={7}>No quotes match these filters.</td></tr>
            )}
            {quotes.map((q) => (
              <tr key={q.id} className="border-b border-black/5 last:border-0 hover:bg-black/[0.02]">
                <td className="px-4 py-3">
                  <Link to="/admin/quotes/$id" params={{ id: q.id }} className="font-semibold text-black hover:text-[#C6A15B]">
                    {q.quoteNumber}
                  </Link>
                  <div className="text-xs text-black/45">{q.items.length} line{q.items.length === 1 ? "" : "s"}</div>
                </td>
                <td className="px-4 py-3">{q.companyName}<div className="text-xs text-black/45">{q.gstNumber ?? "No GSTIN"}</div></td>
                <td className="px-4 py-3">{q.contactName}<div className="text-xs text-black/45">{q.contactEmail}</div></td>
                <td className="px-4 py-3">{QUOTE_STATUS_LABEL[q.status]}</td>
                <td className="px-4 py-3 text-black/60">{q.salesRepEmail ?? "Unassigned"}</td>
                <td className="px-4 py-3 text-black/60">{formatDate(q.createdAt)}</td>
                <td className="px-4 py-3 text-right font-medium">{formatPaise(q.totalCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {d && (d.topCustomers.length > 0 || d.topPacks.length > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="Top corporate customers">
            {d.topCustomers.map((c) => (
              <Row key={c.companyName} left={c.companyName} mid={`${c.quotes} quotes`} right={formatPaise(c.valueCents)} />
            ))}
          </Panel>
          <Panel title="Top corporate packs">
            {d.topPacks.map((p) => (
              <Row key={p.packName} left={p.packName} mid={`${p.qty} units`} right={formatPaise(p.valueCents)} />
            ))}
          </Panel>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-sm border border-black/10 bg-white p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">{label}</p>
      <p className="mt-2 text-xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-sm border border-black/10 bg-white p-4">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">{title}</h3>
      <div className="mt-3 space-y-2">{children}</div>
    </div>
  );
}

function Row({ left, mid, right }: { left: string; mid: string; right: string }) {
  return (
    <div className="flex items-center justify-between border-b border-black/5 pb-2 text-sm last:border-0">
      <span>{left}</span>
      <span className="text-xs text-black/45">{mid}</span>
      <span className="font-medium">{right}</span>
    </div>
  );
}